// Community-editable course metadata: enum id -> label maps and section layout.
// Mirrors the Android app (base/domain CourseMetadata enums + courses_metadata_* strings and
// the five sections in CourseMetadataSection). Enum ids are the stable wire contract; unknown
// ids are dropped for forward compatibility.

const TEE_TYPES = {
  1: 'Concrete',
  2: 'Asphalt',
  3: 'Gravel',
  4: 'Dirt / Natural',
  5: 'Grass',
  6: 'Rubber mat',
  7: 'Artificial turf',
  8: 'Wood / Deck',
  9: 'Throw from ground',
  10: 'Mixed',
}

const BASKET_TYPES = {
  1: 'DGA',
  2: 'Innova DISCatcher',
  3: 'Discmania',
  4: 'Latitude 64 / Dynamic Discs',
  5: 'MVP / Axiom',
  6: 'Prodigy',
  7: 'Discraft',
  8: 'Gateway',
  9: 'Homemade / Custom',
  10: 'Mixed / Various',
  11: 'Unknown',
}

const TERRAIN = {
  1: 'Wooded',
  2: 'Open field',
  3: 'Park',
  4: 'Hilly',
  5: 'Flat',
  6: 'Mountainous',
  7: 'Wetland / Swamp',
  8: 'Desert',
  9: 'Coastal',
  10: 'Urban',
}

const WATER_HAZARDS = { 1: 'None', 2: 'Some', 3: 'Significant — easy to lose discs' }
const LEVEL = { 1: 'Easy', 2: 'Medium', 3: 'Hard', 4: 'Pro' }
const TOILETS = { 1: 'None', 2: 'Free', 3: 'Paid' }
const RESTAURANT = { 1: 'None', 2: 'On-site', 3: 'Nearby' }
const PRO_SHOP = { 1: 'None', 2: 'Permanent', 3: 'Weekends / Seasonal', 4: 'Events only' }
const PARKING = { 1: 'None / Street', 2: 'Free lot', 3: 'Paid lot' }
const FEE_TO_PLAY = { 1: 'Free', 2: 'Pay to play', 3: 'Membership / Club' }
const COURSE_TYPE = { 1: 'Permanent', 2: 'Seasonal', 3: 'Temporary' }

/**
 * Every metadata field, in the order and the sections the course page shows them in.
 *
 * One declaration serving two very different readers: the public course page, which renders whatever
 * is populated, and the curation survivor editor, which has to offer *every* field as something you
 * can pick a value for. Those drifted apart the moment they were written twice, so they are not.
 *
 * `type` is what a field is, not how it looks: `multi` is a set of enum ids, `enum` a single one,
 * `bool` is tri-state (true / false / null = unknown), `block` is long free text, `year` a number.
 */
export const METADATA_FIELDS = [
  { key: 'teeTypes', label: 'Tee types', type: 'multi', options: TEE_TYPES, section: 'Course setup' },
  { key: 'basketTypes', label: 'Basket types', type: 'multi', options: BASKET_TYPES, section: 'Course setup' },
  { key: 'multipleTees', label: 'Multiple tees', type: 'bool', section: 'Course setup' },
  { key: 'multiplePinPositions', label: 'Multiple pin positions', type: 'bool', section: 'Course setup' },
  { key: 'courseType', label: 'Course type', type: 'enum', options: COURSE_TYPE, section: 'Course setup' },
  { key: 'level', label: 'Difficulty level', type: 'enum', options: LEVEL, section: 'Course setup' },

  { key: 'terrain', label: 'Terrain', type: 'multi', options: TERRAIN, section: 'Terrain & conditions' },
  { key: 'waterHazards', label: 'Water hazards', type: 'enum', options: WATER_HAZARDS, section: 'Terrain & conditions' },
  { key: 'lighting', label: 'Lit for night play', type: 'bool', section: 'Terrain & conditions' },

  { key: 'toilets', label: 'Toilets', type: 'enum', options: TOILETS, section: 'Facilities' },
  { key: 'drinkingWater', label: 'Drinking water', type: 'bool', section: 'Facilities' },
  { key: 'restaurant', label: 'Restaurant / café', type: 'enum', options: RESTAURANT, section: 'Facilities' },
  { key: 'restaurantUrl', label: 'Restaurant link', type: 'url', section: 'Facilities' },
  { key: 'proShop', label: 'Disc golf shop', type: 'enum', options: PRO_SHOP, section: 'Facilities' },
  { key: 'proShopUrl', label: 'Shop link', type: 'url', section: 'Facilities' },
  { key: 'practiceArea', label: 'Practice basket / putting green', type: 'bool', section: 'Facilities' },
  { key: 'camping', label: 'Camping', type: 'bool', section: 'Facilities' },
  { key: 'signage', label: 'Tee signs', type: 'bool', section: 'Facilities' },

  { key: 'parking', label: 'Parking', type: 'enum', options: PARKING, section: 'Access' },
  { key: 'feeToPlay', label: 'Fee to play', type: 'enum', options: FEE_TO_PLAY, section: 'Access' },
  { key: 'isPrivate', label: 'Private course', type: 'bool', section: 'Access' },
  { key: 'handicapAccessible', label: 'Wheelchair accessible', type: 'bool', section: 'Access' },
  { key: 'dogFriendly', label: 'Dog friendly', type: 'bool', section: 'Access' },
  { key: 'cartFriendly', label: 'Cart accessible', type: 'bool', section: 'Access' },
  { key: 'strollerFriendly', label: 'Stroller accessible', type: 'bool', section: 'Access' },

  { key: 'clubName', label: 'Club / community', type: 'text', section: 'About & contact' },
  { key: 'yearEstablished', label: 'Year established', type: 'year', section: 'About & contact' },
  { key: 'homepageUrl', label: 'Course / club homepage', type: 'url', section: 'About & contact' },
  {
    key: 'maintenanceNotes',
    label: 'Maintenance & things to watch out for',
    type: 'block',
    section: 'About & contact',
  },
  { key: 'info', label: 'Info', type: 'block', section: 'About & contact' },
]

/** Section order for the course page. Nothing outside these is rendered. */
export const METADATA_SECTIONS = [
  'Course setup',
  'Terrain & conditions',
  'Facilities',
  'Access',
  'About & contact',
]

// Map a list of enum ids to their labels, dropping unknown ids.
const labels = (ids, map) => (ids || []).map((id) => map[id]).filter(Boolean)

// A single value enum id -> label (or null when absent/unknown).
const label = (id, map) => (id != null ? map[id] || null : null)

// Row builders return null when the field carries no information, so sections can drop them.
const chips = (label, values) =>
  values && values.length > 0 ? { type: 'chips', label, values } : null
const value = (label, v) => (v ? { type: 'value', label, value: v } : null)
const bool = (label, v) =>
  v === true || v === false ? { type: 'bool', label, value: v } : null
const link = (label, url) =>
  url && url.trim() ? { type: 'link', label, url: url.trim() } : null
const block = (label, v) =>
  v && v.trim() ? { type: 'block', label, value: v.trim() } : null

// One field of a metadata record as a displayable row, or null when it carries no information.
const rowFor = (field, v) => {
  switch (field.type) {
    case 'multi':
      return chips(field.label, labels(v, field.options))
    case 'enum':
      return value(field.label, label(v, field.options))
    case 'bool':
      return bool(field.label, v)
    case 'url':
      return link(field.label, v)
    case 'block':
      return block(field.label, v)
    case 'year':
      return value(field.label, v ? '' + v : null)
    default:
      return value(field.label, v)
  }
}

/**
 * Builds the displayable metadata sections from a CourseMetadataDTO, keeping only populated
 * rows and dropping any section that ends up empty. Returns [] when there's nothing to show.
 */
export const buildMetadataSections = (m) => {
  if (!m) return []

  return METADATA_SECTIONS.map((title) => ({
    title,
    rows: METADATA_FIELDS.filter((field) => field.section === title)
      .map((field) => rowFor(field, m[field.key]))
      .filter(Boolean),
  })).filter((section) => section.rows.length > 0)
}
